/**
 * References (Labs) — outside TikToks and Reels pasted into Hook studio and read by the reference analyser (backend
 * referenceAnalyser.ts): the hook (first 3 s), format, message, delivery, on-screen text and CTA, with the post's
 * public numbers. The outside shelf of the hook library: competitor ads and viral creator videos to borrow a move
 * from — never footage to reuse, so they are never recast.
 *
 * A paste returns at once; the cards fill in while the backend fetches, watches and reads (polled every 4 s).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Link2, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type ContentReference } from '@/cowork/omniad/api/coworkApi';
import { linksIn } from './videoStudioShared';

const compact = (n?: number | null) => (n == null ? '–' : Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n));
const pct = (n?: number | null) => (n == null ? '–' : `${(n * 100).toFixed(n < 0.1 ? 1 : 0)}%`);
const MAX_URLS = 10;

/** Where a reading reference is, in words. */
const STEP: Record<ContentReference['status'], string> = { queued: 'Queued', fetching: 'Fetching the post', watching: 'Watching the video', analysing: 'Reading it', ready: '', error: '' };
const busy = (r: ContentReference) => r.status !== 'ready' && r.status !== 'error';

const FIELDS: Array<[keyof NonNullable<ContentReference['analysis']>, string]> = [
  ['hook', 'Hook (first 3 s)'], ['format', 'Format'], ['message', 'Message'], ['delivery', 'Delivery'], ['onScreenText', 'On screen'], ['cta', 'CTA'],
];

function ReferenceCard({ r }: { r: ContentReference }) {
  const m = r.metrics;
  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-[#eaecf0] bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#7a1fbf]">{r.platform === 'TIKTOK' ? 'TikTok' : 'Instagram'}</span>
        <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex min-w-0 items-center gap-1 truncate text-[11.5px] text-[#98a2b3] hover:text-[#475467]">
          {r.handle ? `@${r.handle}` : 'Open post'}<ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      </div>
      {r.videoUrl
        ? <video src={`${r.videoUrl}#t=0.1`} controls playsInline preload="metadata" className="aspect-[9/16] w-full rounded-lg bg-[#101828] object-cover" />
        : r.coverUrl ? <img src={r.coverUrl} alt="" className="aspect-[9/16] w-full rounded-lg bg-[#f2f4f7] object-cover" /> : null}
      {busy(r) && <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[#475467]"><Loader2 className="h-3.5 w-3.5 animate-spin" />{STEP[r.status]}…</span>}
      {r.status === 'error' && <span className="flex items-start gap-1.5 text-[12px] text-[#b42318]"><TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />{r.error || 'The read failed.'}</span>}
      {r.analysis && (
        <dl className="flex flex-col gap-1.5">
          {FIELDS.map(([k, label]) => r.analysis![k] ? (
            <div key={k}><dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#98a2b3]">{label}</dt><dd className="text-[12.5px] leading-snug text-[#344054]">{r.analysis![k]}</dd></div>
          ) : null)}
        </dl>
      )}
      {r.depth === 'caption' && <span className="text-[11.5px] text-[#b54708]">Caption only: the video could not be watched.</span>}
      {m.views != null && (
        <span className="text-[11.5px] tabular-nums text-[#667085]">{compact(m.views)} views · ERI {pct(m.eri)}{m.virality != null ? ` · ${m.virality.toFixed(1)}× followers` : ''}</span>
      )}
      {r.productId && <span className="text-[11.5px] font-semibold text-[#067647]">One of our posts: its campaign results are in Creative intelligence.</span>}
    </div>
  );
}

export function ReferenceShelf({ brandId }: { brandId?: string }) {
  const q = useQuery({
    queryKey: ['references', brandId || 'house'], queryFn: () => coworkApi.insights.references(brandId),
    refetchInterval: (x) => (x.state.data?.references.some(busy) ? 4000 : false),
  });
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const links = linksIn(text);

  const read = async () => {
    setSending(true);
    try {
      const r = await coworkApi.insights.addReferences({ urls: links, brandId });
      const fresh = r.references.filter(busy).length;
      if (r.rejected.length) toast.message(`${r.rejected.length} link${r.rejected.length > 1 ? 's' : ''} skipped: ${r.rejected[0].why}`);
      if (r.references.length) toast.success(fresh ? `Reading ${fresh} video${fresh > 1 ? 's' : ''}: about a minute each.` : 'Already read: shown below.');
      setText('');
      void q.refetch();
    } catch (e) { toast.error((e as Error).message); } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <h2 className="flex items-center gap-1.5 text-[14px] font-semibold text-[#101828]">
        <Link2 className="h-4 w-4 text-[#7a1fbf]" />References <span className="font-normal text-[#98a2b3]">· {q.data?.references.length ?? 0}</span>
        <span className="rounded-full bg-[#f4ebff] px-2 py-0.5 text-[10.5px] font-semibold text-[#7a1fbf]">Labs</span>
      </h2>
      <p className="text-[12px] text-[#667085]">Paste competitor ads or viral TikToks and Reels: each is watched and read into its hook, format, message and delivery, with its public numbers. Borrow the move; never the post.</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} aria-label="Links to read"
          placeholder="https://www.tiktok.com/@creator/video/…, one link per line, up to 10"
          className="min-h-[44px] flex-1 rounded-lg border border-[#d0d5dd] bg-white px-3 py-2 text-[13px] text-[#101828] placeholder:text-[#98a2b3] focus:border-[#be43ff] focus:outline-none" />
        <button type="button" disabled={!links.length || links.length > MAX_URLS || sending} onClick={() => void read()}
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-[#2e024c] px-3 py-2 text-[13px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40">
          {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{links.length > MAX_URLS ? `At most ${MAX_URLS} links` : links.length > 1 ? `Read ${links.length} videos` : 'Read the video'}
        </button>
      </div>
      {q.isError ? <p className="rounded-xl bg-[#f9fafb] px-4 py-3 text-[13px] text-[#475467]">{(q.error as Error).message}</p>
        : q.data?.references.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {q.data.references.map((r) => <ReferenceCard key={r.id} r={r} />)}
          </div>
        ) : q.isLoading ? <Loader2 className="h-5 w-5 animate-spin text-[#98a2b3]" /> : null}
    </div>
  );
}
